import type { StopSuggestion } from '../types/pages'
import type { StopReason, StopReasonCode } from '../types/requests'

/** De redenen die de klant zelf kiest (niet `door-salon`). */
export type CustomerStopReason = Exclude<StopReasonCode, 'door-salon'>

/**
 * De vaste lijst redenen bij stoppen, in deze volgorde. De teksten zijn aan te passen in
 * Paginateksten (`pages.portal.stopReasons`); code en voorstel liggen vast, zodat Inzicht ze kan tellen.
 */
export const STOP_REASONS: readonly { code: CustomerStopReason; label: string; suggest: StopSuggestion }[] = [
  { code: 'genoeg', label: 'Ik heb nog genoeg', suggest: 'pauze' },
  { code: 'te-duur', label: 'Het is te duur', suggest: 'geen' },
  { code: 'huid', label: 'Mijn huid reageert niet goed', suggest: 'sarah' },
  { code: 'iets-anders', label: 'Ik probeer iets anders', suggest: 'geen' },
  { code: 'anders', label: 'Andere reden', suggest: 'geen' },
]

/** "Andere reden": hoogstens zoveel tekens toelichting. */
export const STOP_TEXT_MAX = 200

/** De tekst voor een reden: uit Paginateksten, anders de standaard. */
export function stopReasonLabel(code: StopReasonCode, labels?: Partial<Record<CustomerStopReason, string>> | null): string {
  if (code === 'door-salon') return 'Gestopt door de salon'
  const own = labels && typeof labels === 'object' && !Array.isArray(labels) ? labels[code] : undefined
  return (typeof own === 'string' && own.trim()) || STOP_REASONS.find((r) => r.code === code)?.label || code
}

export type StopReasonTexts = Record<CustomerStopReason, string>

/**
 * De teksten in de vorm van nu, ook uit een oudere opslag. Vroeger was het een lijst
 * `{ label, suggest }[]` in de volgorde van STOP_REASONS: die wordt op positie overgenomen.
 * Een ontbrekende of lege tekst = de standaard.
 */
export function stopReasonTexts(raw: unknown): StopReasonTexts {
  const fromList = Array.isArray(raw)
    ? (raw as unknown[]).map((r) => (r && typeof r === 'object' && typeof (r as { label?: unknown }).label === 'string' ? (r as { label: string }).label : ''))
    : null
  const record = !fromList && raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {}
  return Object.fromEntries(
    STOP_REASONS.map((r, i) => {
      const own = fromList ? fromList[i] : record[r.code]
      return [r.code, typeof own === 'string' && own.trim() ? own : r.label]
    }),
  ) as StopReasonTexts
}

/** Wat er naar de server gaat: geen reden, of een vaste code met (alleen bij "anders") een ingekorte tekst. */
export function stopReasonFor(code: CustomerStopReason | null, text: string): StopReason | undefined {
  if (!code) return undefined
  const clean = text.replace(/\s+/g, ' ').trim().slice(0, STOP_TEXT_MAX)
  return code === 'anders' && clean ? { code, text: clean } : { code }
}
